/**
 * Mensagens do WebSocket.
 *
 * O envelope é `{ t, id?, ch?, seq?, p }` (WEBSOCKET 2). `id` vai do cliente
 * para o servidor e volta dentro do `ack`; `ch` e `seq` vêm do servidor. O
 * `seq` cresce por canal e é o que permite pedir replay depois de uma queda:
 * o cliente reenvia `sub` com `since` igual ao último `seq` que recebeu e o
 * servidor devolve o que faltou, ou `resync` quando o pedido é antigo demais
 * (WEBSOCKET 3 e 6).
 *
 * Toda mensagem que chega passa por aqui antes de qualquer processamento, como
 * manda SECURITY 4 e 8.
 */
import { z } from "zod";
import { EntitlementsSchema } from "./auth.js";
import { IdSchema, IsoDateTimeSchema, MoneySchema } from "./common.js";
import { ErrorCodeSchema } from "./errors.js";
import { LeagueLogEntrySchema, LeagueStateSchema } from "./leagues.js";
import { AuctionSchema, LeagueOfferSchema, LeagueOfferStatusSchema } from "./market.js";
import { LineupSchema, MatchFactorSchema, MatchSideStatsSchema } from "./world.js";
import { CHAT_MAX_LENGTH } from "../constants.js";
// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------
/**
 * Nome de canal: `user:{id}`, `league:{id}`, `match:{id}`, `challenge:{id}` ou
 * o canal fixo `ranked:queue` (WEBSOCKET 3).
 */
export const ChannelSchema = z
    .string()
    .regex(/^(?:(?:user|league|match|challenge):[A-Za-z0-9_-]{1,64}|ranked:queue)$/, "Canal desconhecido.");
/** Sequência por canal, crescente, usada na reconexão. */
export const SeqSchema = z.number().int().min(0);
/**
 * Envelope genérico, para quem só precisa saber o tipo e o canal antes de
 * escolher o esquema do payload.
 */
export const EnvelopeSchema = z.object({
    t: z.string().min(1).max(64),
    id: IdSchema.optional(),
    ch: ChannelSchema.optional(),
    seq: SeqSchema.optional(),
    p: z.unknown(),
});
/** Monta a mensagem completa de um tipo do servidor. */
function serverMessage(t, p) {
    return z.object({
        t: z.literal(t),
        ch: ChannelSchema.optional(),
        seq: SeqSchema.optional(),
        p,
    });
}
/** Monta a mensagem completa de um tipo do cliente. */
function clientMessage(t, p) {
    return z
        .object({
        t: z.literal(t),
        /** Correlaciona o `ack` ou o `err` da resposta. */
        id: IdSchema.optional(),
        p,
    })
        .strict();
}
// ---------------------------------------------------------------------------
// Payloads do servidor
// ---------------------------------------------------------------------------
/** Primeira mensagem da conexão. */
export const HelloPayloadSchema = z.object({
    protocolVersion: z.number().int().positive(),
    serverTime: IsoDateTimeSchema,
    heartbeatMs: z.number().int().positive(),
});
/** Confirmação de uma mensagem do cliente. */
export const AckPayloadSchema = z.object({ id: IdSchema, ok: z.boolean() });
/** Recusa de uma mensagem do cliente, com o mesmo código da API. */
export const ErrPayloadSchema = z.object({
    id: IdSchema,
    code: ErrorCodeSchema,
    message: z.string().min(1),
});
/** O replay pedido é antigo demais. O cliente recarrega pela REST. */
export const ResyncPayloadSchema = z.object({ ch: ChannelSchema });
export const LeagueStatePayloadSchema = z.object({
    state: LeagueStateSchema,
    round: z.number().int().min(0),
    deadline: IsoDateTimeSchema.nullable(),
    readyCount: z.number().int().min(0),
    humanCount: z.number().int().min(0),
});
/** Aviso de prazo, enviado a 24 h, 2 h e 15 min do fim. */
export const DeadlineWarningPayloadSchema = z.object({
    round: z.number().int().min(0),
    deadline: IsoDateTimeSchema,
    minutesLeft: z.number().int().min(0),
});
export const RoundProcessingPayloadSchema = z.object({ round: z.number().int().min(0) });
/** Placar de um jogo, do mandante para o visitante. */
export const ScoreSchema = z.tuple([z.number().int().min(0), z.number().int().min(0)]);
export const RoundProcessedPayloadSchema = z.object({
    round: z.number().int().min(0),
    /** `null` quando meu clube não jogou nesta rodada. */
    myResult: z
        .object({
        matchId: IdSchema,
        /** Nome do clube mandante, pronto para exibição. */
        home: z.string().min(1),
        away: z.string().min(1),
        score: ScoreSchema,
    })
        .nullable(),
    standingsDelta: z.object({ myPosition: z.number().int().min(1) }),
    newsCount: z.number().int().min(0),
});
/** Presença na liga, publicada com agrupamento de dois segundos. */
export const PresencePayloadSchema = z.object({
    online: z.array(IdSchema),
    ready: z.array(IdSchema),
});
export const MemberPayloadSchema = z.object({
    userId: IdSchema,
    clubId: IdSchema.nullable(),
});
/** Pausa, retomada e fim de liga. O motivo aparece quando existe. */
export const LeagueLifecyclePayloadSchema = z.object({
    reason: z.string().max(200).optional(),
});
export const LeagueLogPayloadSchema = z.object({ entry: LeagueLogEntrySchema });
// Partida ao vivo
/**
 * Um lado da partida como o campo desenha: a formação, as vagas dela na ordem
 * e quem está em cada uma, mais o banco.
 *
 * Só o que o desenho precisa. Força e atributos ficam de fora de propósito: o
 * elenco alheio não atravessa a visão filtrada (SERVER 5), e a partida ao vivo
 * não é a exceção.
 */
export const MatchSidePayloadSchema = z.object({
    clubId: IdSchema,
    formation: z.string().min(1),
    slots: z.array(z.string()).max(11),
    custom: z.array(z.object({ pos: z.string(), x: z.number(), y: z.number() })).max(11).nullable(),
    players: z.array(z.object({
        id: IdSchema,
        name: z.string().min(1),
        shirt: z.number().int().nullable(),
        pos: z.string(),
        nationality: z.string().optional(),
    })),
});
export const MatchStartPayloadSchema = z.object({
    matchId: IdSchema,
    home: z.string().min(1),
    away: z.string().min(1),
    lineups: z.object({ home: LineupSchema, away: LineupSchema }),
    /** As escalações desenháveis. Opcional para o cliente antigo continuar lendo o começo. */
    sides: z.object({ home: MatchSidePayloadSchema, away: MatchSidePayloadSchema }).optional(),
});
export const PlayKindSchema = z.enum(["armacao", "drible", "cruzamento", "contra_ataque", "bola_parada", "penalti"]);
/**
 * Tipos de lance transmitidos ao vivo, como WEBSOCKET 4 os lista.
 *
 * Não são os mesmos nomes do `MatchEventKind` do motor, que estão em português
 * e separam cartão amarelo de vermelho e pênalti perdido de gol. A tradução é
 * feita por quem transmite a partida.
 */
export const MatchEventKindSchema = z.enum([
    "goal",
    "chance",
    "card",
    "injury",
    "sub",
    "penalty",
    "foul",
    "offside",
    "commentary",
]);
export const MatchEventPayloadSchema = z.object({
    minute: z.number().int().min(0).max(130),
    kind: MatchEventKindSchema,
    clubId: IdSchema.optional(),
    playerId: IdSchema.nullable().optional(),
    assistId: IdSchema.nullable().optional(),
    /**
     * A narração como chave do catálogo do motor e os valores dela.
     *
     * Era a frase pronta, em português do Brasil, e chegava assim mesmo a quem
     * tinha escolhido outro idioma. Este é o texto que mais aparece durante uma
     * partida, então era também o defeito mais visível.
     */
    key: z.string(),
    params: z.record(z.union([z.string(), z.number()])),
    /** A jogada, nos lances de chance, para o campo desenhar o lance certo. */
    play: PlayKindSchema.optional(),
    /** Quem finalizou, quando `playerId` é o goleiro que defendeu. */
    shooterId: IdSchema.nullable().optional(),
});
/** Janela de substituição. São três por partida. */
export const SubWindowSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export const MatchPausePayloadSchema = z.object({
    minute: z.number().int().min(0).max(130),
    window: SubWindowSchema,
    /** Quanto tempo o treinador tem para decidir, em milissegundos. */
    deadlineMs: z.number().int().min(0),
});
export const MatchEndPayloadSchema = z.object({
    score: ScoreSchema,
    /**
     * Os fatores de maior peso. O documento pede três; o motor calcula até
     * cinco e o servidor manda os que mais explicam o resultado.
     */
    factors: z.array(MatchFactorSchema).max(3),
    stats: z.object({ home: MatchSideStatsSchema, away: MatchSideStatsSchema }),
});
// Mercado
export const OfferReceivedPayloadSchema = z.object({ offer: LeagueOfferSchema });
export const OfferUpdatedPayloadSchema = z.object({
    offerId: IdSchema,
    status: LeagueOfferStatusSchema,
    counterAmount: MoneySchema.optional(),
});
export const AuctionOpenedPayloadSchema = z.object({
    auction: AuctionSchema,
    closesAtRound: z.number().int().min(0),
});
export const AuctionClosedPayloadSchema = z.object({
    auctionId: IdSchema,
    /** `null` quando ninguém deu lance. */
    winnerClubId: IdSchema.nullable(),
    amount: MoneySchema.nullable(),
});
// Chat, social e conta
export const ChatMessagePayloadSchema = z.object({
    id: IdSchema,
    userId: IdSchema,
    text: z.string().min(1).max(CHAT_MAX_LENGTH),
    createdAt: IsoDateTimeSchema,
    kind: z.enum(["user", "system"]),
});
export const ChatTypingPayloadSchema = z.object({ userId: IdSchema });
/** Notificação da central do usuário (DATABASE 2.9). */
export const NotificationSchema = z.object({
    id: IdSchema,
    kind: z.string().min(1),
    payload: z.record(z.unknown()),
    readAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
});
export const NotificationPayloadSchema = z.object({ notification: NotificationSchema });
export const EntitlementsPayloadSchema = z.object({ entitlements: EntitlementsSchema });
/** Sessão revogada. Sem payload: o cliente só precisa sair. */
export const SessionRevokedPayloadSchema = z.object({});
// ---------------------------------------------------------------------------
// Mensagens do servidor
// ---------------------------------------------------------------------------
export const HelloMessageSchema = serverMessage("hello", HelloPayloadSchema);
export const AckMessageSchema = serverMessage("ack", AckPayloadSchema);
export const ErrMessageSchema = serverMessage("err", ErrPayloadSchema);
export const ResyncMessageSchema = serverMessage("resync", ResyncPayloadSchema);
export const LeagueStateMessageSchema = serverMessage("league.state", LeagueStatePayloadSchema);
export const LeagueDeadlineWarningMessageSchema = serverMessage("league.deadline.warning", DeadlineWarningPayloadSchema);
export const LeagueRoundProcessingMessageSchema = serverMessage("league.round.processing", RoundProcessingPayloadSchema);
export const LeagueRoundProcessedMessageSchema = serverMessage("league.round.processed", RoundProcessedPayloadSchema);
export const LeaguePresenceMessageSchema = serverMessage("league.presence", PresencePayloadSchema);
export const LeagueMemberJoinedMessageSchema = serverMessage("league.member.joined", MemberPayloadSchema);
export const LeagueMemberLeftMessageSchema = serverMessage("league.member.left", MemberPayloadSchema);
export const LeagueMemberKickedMessageSchema = serverMessage("league.member.kicked", MemberPayloadSchema);
export const LeaguePausedMessageSchema = serverMessage("league.paused", LeagueLifecyclePayloadSchema);
export const LeagueResumedMessageSchema = serverMessage("league.resumed", LeagueLifecyclePayloadSchema);
export const LeagueFinishedMessageSchema = serverMessage("league.finished", LeagueLifecyclePayloadSchema);
export const LeagueLogMessageSchema = serverMessage("league.log", LeagueLogPayloadSchema);
export const MatchStartMessageSchema = serverMessage("match.start", MatchStartPayloadSchema);
export const MatchEventMessageSchema = serverMessage("match.event", MatchEventPayloadSchema);
export const MatchPauseMessageSchema = serverMessage("match.pause", MatchPausePayloadSchema);
export const MatchEndMessageSchema = serverMessage("match.end", MatchEndPayloadSchema);
export const MarketOfferReceivedMessageSchema = serverMessage("market.offer.received", OfferReceivedPayloadSchema);
export const MarketOfferUpdatedMessageSchema = serverMessage("market.offer.updated", OfferUpdatedPayloadSchema);
export const MarketAuctionOpenedMessageSchema = serverMessage("market.auction.opened", AuctionOpenedPayloadSchema);
export const MarketAuctionClosedMessageSchema = serverMessage("market.auction.closed", AuctionClosedPayloadSchema);
export const ChatMessageMessageSchema = serverMessage("chat.message", ChatMessagePayloadSchema);
export const ChatTypingMessageSchema = serverMessage("chat.typing", ChatTypingPayloadSchema);
export const UserNotificationMessageSchema = serverMessage("user.notification", NotificationPayloadSchema);
export const UserEntitlementsMessageSchema = serverMessage("user.entitlements", EntitlementsPayloadSchema);
export const UserSessionRevokedMessageSchema = serverMessage("user.session.revoked", SessionRevokedPayloadSchema);
/** Qualquer mensagem que o servidor envia, discriminada por `t`. */
export const ServerMessageSchema = z.discriminatedUnion("t", [
    HelloMessageSchema,
    AckMessageSchema,
    ErrMessageSchema,
    ResyncMessageSchema,
    LeagueStateMessageSchema,
    LeagueDeadlineWarningMessageSchema,
    LeagueRoundProcessingMessageSchema,
    LeagueRoundProcessedMessageSchema,
    LeaguePresenceMessageSchema,
    LeagueMemberJoinedMessageSchema,
    LeagueMemberLeftMessageSchema,
    LeagueMemberKickedMessageSchema,
    LeaguePausedMessageSchema,
    LeagueResumedMessageSchema,
    LeagueFinishedMessageSchema,
    LeagueLogMessageSchema,
    MatchStartMessageSchema,
    MatchEventMessageSchema,
    MatchPauseMessageSchema,
    MatchEndMessageSchema,
    MarketOfferReceivedMessageSchema,
    MarketOfferUpdatedMessageSchema,
    MarketAuctionOpenedMessageSchema,
    MarketAuctionClosedMessageSchema,
    ChatMessageMessageSchema,
    ChatTypingMessageSchema,
    UserNotificationMessageSchema,
    UserEntitlementsMessageSchema,
    UserSessionRevokedMessageSchema,
]);
// ---------------------------------------------------------------------------
// Mensagens do cliente
// ---------------------------------------------------------------------------
/** Inscrição em um canal, com pedido de replay a partir de um `seq`. */
export const SubPayloadSchema = z
    .object({
    ch: ChannelSchema,
    /** Último `seq` recebido. Zero pede tudo que o canal ainda guarda. */
    since: SeqSchema.optional(),
})
    .strict();
export const UnsubPayloadSchema = z.object({ ch: ChannelSchema }).strict();
export const ReadyPayloadSchema = z
    .object({ leagueId: IdSchema, ready: z.boolean() })
    .strict();
/** Substituição dentro da janela de pausa do modo ao vivo. */
export const MatchSubPayloadSchema = z
    .object({
    matchId: IdSchema,
    out: IdSchema,
    in: IdSchema,
    window: SubWindowSchema,
})
    .strict();
/**
 * O ajuste tático do intervalo: formação nova e a ordem das vagas, com os
 * mesmos que estão em campo. Só vale na janela dos 45 minutos.
 */
export const MatchShapePayloadSchema = z
    .object({
    matchId: IdSchema,
    formation: z.string().min(1).max(40),
    starters: z.array(IdSchema).min(7).max(11),
    window: SubWindowSchema,
})
    .strict();
export const ChatSendPayloadSchema = z
    .object({
    leagueId: IdSchema,
    text: z.string().trim().min(1).max(CHAT_MAX_LENGTH),
})
    .strict();
export const ChatTypingRequestPayloadSchema = z.object({ leagueId: IdSchema }).strict();
export const PingPayloadSchema = z.object({}).strict();
export const SubMessageSchema = clientMessage("sub", SubPayloadSchema);
export const UnsubMessageSchema = clientMessage("unsub", UnsubPayloadSchema);
export const LeagueReadyMessageSchema = clientMessage("league.ready", ReadyPayloadSchema);
export const MatchSubMessageSchema = clientMessage("match.sub", MatchSubPayloadSchema);
export const MatchShapeMessageSchema = clientMessage("match.shape", MatchShapePayloadSchema);
export const ChatSendMessageSchema = clientMessage("chat.send", ChatSendPayloadSchema);
export const ChatTypingRequestMessageSchema = clientMessage("chat.typing", ChatTypingRequestPayloadSchema);
export const PingMessageSchema = clientMessage("ping", PingPayloadSchema);
/**
 * Qualquer mensagem que o cliente envia.
 *
 * Decisão pesada, como escalação, proposta ou obra, não entra aqui: vai pela
 * REST, para que validação, idempotência e auditoria fiquem em um caminho só
 * (WEBSOCKET 5).
 */
export const ClientMessageSchema = z.discriminatedUnion("t", [
    SubMessageSchema,
    UnsubMessageSchema,
    LeagueReadyMessageSchema,
    MatchSubMessageSchema,
    MatchShapeMessageSchema,
    ChatSendMessageSchema,
    ChatTypingRequestMessageSchema,
    PingMessageSchema,
]);
//# sourceMappingURL=ws.js.map